import {useState} from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import {dataClient} from '../data/dataClient';
import {ingestKnowledgeSource} from '../services/api';
import {BotAvatar} from './BotComponents';

const {
  FiUploadCloud,
  FiFile,
  FiEye,
  FiCheck,
  FiGrid,
  FiList,
  FiUsers,
  FiCheckSquare,
  FiX
} = FiIcons;

export function FileUploader({bots = []}) {
  const [uploading, setUploading] = useState(false);
  const [selectedBotIds, setSelectedBotIds] = useState(bots.slice(0, 3).map((b) => b.id));
  const [tagInput, setTagInput] = useState('');

  async function upload(event) {
    const file = event.target.files?.[0];
    if (!file) return;
    setUploading(true);

    const tags = tagInput
      .split(',')
      .map((t) => t.trim().toLowerCase())
      .filter(Boolean);
    if (!tags.includes('library')) tags.push('library');

    const sizeStr = `${Math.max(1, Math.round(file.size / 1024))} KB`;

    const fileRec = await dataClient.create('files', {
      name: file.name,
      size: sizeStr,
      tags,
      visibility: selectedBotIds
    });

    // Ingest into knowledgeSources for each visible bot
    for (const botId of selectedBotIds) {
      await ingestKnowledgeSource({
        business: dataClient.peek().business?.id,
        bot: botId,
        sourceType: 'file',
        sourceRef: fileRec.id,
        name: file.name
      });
    }

    setUploading(false);
    event.target.value = '';
    setTagInput('');
  }

  function toggleBotSelection(botId) {
    setSelectedBotIds((prev) =>
      prev.includes(botId) ? prev.filter((id) => id !== botId) : [...prev, botId]
    );
  }

  return (
    <div className="panel file-uploader-panel">
      <label className="file-uploader">
        <SafeIcon icon={FiUploadCloud} />
        <b>{uploading ? 'Ingesting into Knowledge Base…' : 'Choose a file to upload'}</b>
        <small>Files persist client-side in Dexie and feed the Knowledge Base for permitted bots.</small>
        <input type="file" onChange={upload} disabled={uploading} />
      </label>

      <div style={{marginTop: '16px'}}>
        <label className="filter">
          Tags (comma-separated)
          <input
            value={tagInput}
            onChange={(e) => setTagInput(e.target.value)}
            placeholder="e.g. policy, pricing, wholesale"
          />
        </label>

        <div style={{marginTop: '12px'}}>
          <small className="muted" style={{display: 'block', marginBottom: '6px'}}>
            Initial bot access permissions:
          </small>
          <div className="tag-chips">
            {bots.map((b) => {
              const active = selectedBotIds.includes(b.id);
              return (
                <button
                  key={b.id}
                  type="button"
                  className={`chip ${active ? '' : 'read-only'}`}
                  onClick={() => toggleBotSelection(b.id)}
                  style={{cursor: 'pointer'}}
                >
                  <SafeIcon icon={active ? FiCheck : FiEye} />
                  {b.name}
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

export function FileList({files = [], bots = []}) {
  const [viewMode, setViewMode] = useState('list'); // 'list' | 'grid'
  const [selectedTag, setSelectedTag] = useState('all');
  const [editingFileId, setEditingFileId] = useState(null);

  // Compute unique tags across files
  const allTags = new Set(['all']);
  files.forEach((f) => (f.tags || []).forEach((t) => allTags.add(t)));

  const filteredFiles = files.filter((f) => {
    if (selectedTag === 'all') return true;
    return (f.tags || []).includes(selectedTag);
  });

  async function toggleFileBotVisibility(file, botId) {
    const current = file.visibility || [];
    const updated = current.includes(botId)
      ? current.filter((id) => id !== botId)
      : [...current, botId];

    await dataClient.update('files', file.id, {visibility: updated});
  }

  return (
    <div className="file-library-wrap">
      {/* Toolbar: Tag filters + View switcher */}
      <div className="inline spread" style={{marginBottom: '16px', flexWrap: 'wrap', gap: '10px'}}>
        <div className="tag-chips">
          {[...allTags].map((tag) => (
            <button
              key={tag}
              type="button"
              className={`chip ${selectedTag === tag ? '' : 'read-only'}`}
              onClick={() => setSelectedTag(tag)}
              style={{cursor: 'pointer', textTransform: 'capitalize'}}
            >
              #{tag}
            </button>
          ))}
        </div>

        <div className="inline">
          <button
            type="button"
            className={`icon-button ${viewMode === 'list' ? 'active' : ''}`}
            onClick={() => setViewMode('list')}
            title="List view"
            aria-label="List view"
          >
            <SafeIcon icon={FiList} />
          </button>
          <button
            type="button"
            className={`icon-button ${viewMode === 'grid' ? 'active' : ''}`}
            onClick={() => setViewMode('grid')}
            title="Grid view"
            aria-label="Grid view"
          >
            <SafeIcon icon={FiGrid} />
          </button>
        </div>
      </div>

      {/* Content Rendering: List vs Grid */}
      {viewMode === 'grid' ? (
        <div className="files-cards-grid">
          {filteredFiles.map((file) => {
            const isEditing = editingFileId === file.id;
            return (
              <article className="file-grid-card" key={file.id}>
                <div className="inline spread">
                  <span className="file-icon"><SafeIcon icon={FiFile} /></span>
                  <small className="muted">{file.size}</small>
                </div>
                <b>{file.name}</b>
                <div className="tag-row">
                  {(file.tags || []).map((t) => <span key={t}>#{t}</span>)}
                </div>

                <div className="visibility-control-strip">
                  <button
                    type="button"
                    className="button ghost compact"
                    onClick={() => setEditingFileId(isEditing ? null : file.id)}
                    style={{fontSize: '11px', padding: '2px 6px'}}
                  >
                    <SafeIcon icon={FiEye} />
                    {file.visibility?.length || 0} bots permitted
                  </button>

                  {isEditing && (
                    <div className="visibility-popover">
                      <div className="inline spread" style={{marginBottom: '6px'}}>
                        <small><b>Permissions:</b></small>
                        <button type="button" onClick={() => setEditingFileId(null)} className="icon-button" style={{width: '20px', height: '20px'}}>
                          <SafeIcon icon={FiX} />
                        </button>
                      </div>
                      {bots.map((b) => {
                        const permitted = (file.visibility || []).includes(b.id);
                        return (
                          <label key={b.id} className="check-row" style={{padding: '3px 0', fontSize: '11px'}}>
                            <input
                              type="checkbox"
                              checked={permitted}
                              onChange={() => toggleFileBotVisibility(file, b.id)}
                            />
                            <span>{b.name}</span>
                          </label>
                        );
                      })}
                    </div>
                  )}
                </div>
              </article>
            );
          })}
        </div>
      ) : (
        <div className="file-list">
          {filteredFiles.map((file) => {
            const isEditing = editingFileId === file.id;
            return (
              <article className="file-row" key={file.id}>
                <span className="file-icon">
                  <SafeIcon icon={FiFile} />
                </span>
                <div>
                  <b>{file.name}</b>
                  <small>{file.size}</small>
                  <div className="tag-row">
                    {(file.tags || []).map((tag) => (
                      <span key={tag}>#{tag}</span>
                    ))}
                  </div>
                </div>

                <div className="visibility-cell" style={{position: 'relative'}}>
                  <button
                    type="button"
                    className="button ghost compact visibility-btn"
                    onClick={() => setEditingFileId(isEditing ? null : file.id)}
                    title="Click to edit bot permissions"
                  >
                    <SafeIcon icon={FiEye} />
                    <span>
                      {(file.visibility || []).length
                        ? (file.visibility || [])
                            .map((id) => bots.find((b) => b.id === id)?.name)
                            .filter(Boolean)
                            .join(', ')
                        : 'No bots (private)'}
                    </span>
                  </button>

                  {isEditing && (
                    <div className="visibility-popover" style={{position: 'absolute', right: 0, top: '100%', zIndex: 40, background: 'white', border: '1px solid var(--color-line)', borderRadius: 'var(--radius-md)', padding: '12px', boxShadow: 'var(--shadow-md)', width: '220px'}}>
                      <div className="inline spread" style={{marginBottom: '6px'}}>
                        <span className="eyebrow" style={{fontSize: '10px'}}>Bot access</span>
                        <button type="button" onClick={() => setEditingFileId(null)} className="icon-button" style={{width: '20px', height: '20px'}}>
                          <SafeIcon icon={FiX} />
                        </button>
                      </div>
                      {bots.map((b) => {
                        const permitted = (file.visibility || []).includes(b.id);
                        return (
                          <label key={b.id} className="check-row" style={{padding: '4px 0', fontSize: '12px'}}>
                            <input
                              type="checkbox"
                              checked={permitted}
                              onChange={() => toggleFileBotVisibility(file, b.id)}
                            />
                            <span>{b.name} <small className="muted">({b.role})</small></span>
                          </label>
                        );
                      })}
                    </div>
                  )}
                </div>
              </article>
            );
          })}
        </div>
      )}
    </div>
  );
}

export function ReverseBotView({bots = [], files = []}) {
  return (
    <div className="reverse-bot-view">
      <div className="section-heading">
        <div>
          <span className="eyebrow">Reverse view</span>
          <h3>Knowledge access by Bot</h3>
        </div>
        <small className="muted">Verify which files are loaded into each bot’s context</small>
      </div>

      <div className="bot-access-grid" style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '16px'}}>
        {bots.map((bot) => {
          const visibleFiles = files.filter(
            (f) => Array.isArray(f.visibility) && f.visibility.includes(bot.id)
          );
          return (
            <article className="panel bot-access-card" key={bot.id} style={{padding: '16px'}}>
              <div className="inline" style={{gap: '10px', marginBottom: '12px'}}>
                <BotAvatar bot={bot} />
                <div>
                  <b>{bot.name}</b>
                  <small className="muted">{bot.role}</small>
                </div>
                <span className="count" style={{marginLeft: 'auto'}}>{visibleFiles.length}</span>
              </div>

              {bot.knowledgeScope && (
                <div style={{fontSize: '11px', color: 'var(--color-inkSoft)', background: 'var(--color-surfaceAlt)', padding: '6px 10px', borderRadius: 'var(--radius-sm)', marginBottom: '10px'}}>
                  <b>Scope:</b> {bot.knowledgeScope.length > 80 ? `${bot.knowledgeScope.slice(0, 80)}…` : bot.knowledgeScope}
                </div>
              )}

              <div className="bot-file-mini-list" style={{display: 'flex', flexDirection: 'column', gap: '6px'}}>
                {visibleFiles.length === 0 ? (
                  <small className="muted">No files assigned to this bot.</small>
                ) : (
                  visibleFiles.map((f) => (
                    <div key={f.id} className="inline spread" style={{fontSize: '12px', padding: '4px 0', borderBottom: '1px dashed var(--color-line)'}}>
                      <span style={{overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>
                        <SafeIcon icon={FiFile} /> {f.name}
                      </span>
                      <small className="muted">{f.size}</small>
                    </div>
                  ))
                )}
              </div>
            </article>
          );
        })}
      </div>
    </div>
  );
}
